import type { BankStatement } from '@app/domain/entities';
import type { ITransactionCategorizationPort, TransactionCategorizationResult } from '@app/domain/ports';

interface CurrentJson {
  version: string;
  modelUrl: string;
  labelsUrl: string;
}

const CACHE_NAME = 'ml-models';

export class OnnxCategorizationAdapter implements ITransactionCategorizationPort {
  private session: unknown = null;
  private labels: string[] = [];
  private currentVersion: string | null = null;

  constructor(
    private readonly isEnabled: () => boolean = () => true,
    private readonly getCurrentJsonUrl: () => string = () => '/ml/current.json',
  ) {}

  async isAvailable(): Promise<boolean> {
    if (!this.isEnabled()) return false;
    try {
      const response = await fetch(this.bustCache(this.getCurrentJsonUrl()));
      if (!response.ok) return false;
      const current: CurrentJson = await response.json();
      if (!current?.modelUrl) return false;
      // HEAD-check the model binary — handles v0 placeholder where no ONNX is deployed
      const headResponse = await fetch(current.modelUrl, { method: 'HEAD' });
      return headResponse.ok;
    } catch (error) {
      console.warn('Failed to check ONNX model availability', error);
      return false;
    }
  }

  async classify(statements: BankStatement[]): Promise<TransactionCategorizationResult[]> {
    try {
      const session = await this.getSession();
      const ort = await import('onnxruntime-web');
      const inputName = (session as { inputNames: string[] }).inputNames[0];
      const outputName = (session as { outputNames: string[] }).outputNames[0];

      const flat = statements.map((statement) => this.normalizeStatement(statement));
      const tensor = new ort.Tensor('string', flat, [flat.length, 1]);
      const outputMap = await (
        session as {
          run: (feeds: Record<string, unknown>, fetches: string[]) => Promise<Record<string, { data: unknown }>>;
        }
      ).run({ [inputName]: tensor }, [outputName]);

      const labelData = outputMap[outputName].data as string[];

      return labelData.map((predictedLabel: string) => {
        return this.parseLabel(predictedLabel, 1.0);
      });
    } catch (error) {
      console.warn('ONNX categorization failed, falling back to default', error);
      return statements.map(() => ({
        transactionType: 'expense',
        categoryName: '',
        subCategoryName: null,
        confidence: 0,
      }));
    }
  }

  private parseLabel(label: string, confidence: number): TransactionCategorizationResult {
    const firstSlashIdx = label.indexOf('/');
    const prefix = firstSlashIdx === -1 ? null : label.slice(0, firstSlashIdx).toLowerCase();
    const hasTransactionPrefix = prefix === 'expense' || prefix === 'income';
    const transactionType = hasTransactionPrefix ? (prefix as 'expense' | 'income') : 'expense';
    const categoryPath = hasTransactionPrefix ? label.slice(firstSlashIdx + 1) : label;

    const slashIdx = categoryPath.lastIndexOf('/');
    if (slashIdx === -1) {
      return { transactionType, categoryName: categoryPath, subCategoryName: null, confidence };
    }
    return {
      transactionType,
      categoryName: categoryPath.slice(0, slashIdx),
      subCategoryName: categoryPath.slice(slashIdx + 1),
      confidence,
    };
  }

  private normalizeStatement(statement: BankStatement): string {
    const direction = statement.amount < 0 ? 'expense' : 'income';
    return [direction, statement.mcc, statement.description, statement.reference]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .trim();
  }

  private async getSession(): Promise<unknown> {
    const current = await this.fetchCurrentJson();
    if (this.session && this.currentVersion === current.version) {
      return this.session;
    }

    const ort = await import('onnxruntime-web');
    ort.env.wasm.numThreads = 1;
    ort.env.wasm.wasmPaths = '/assets/onnx/';

    const [modelBuffer, labels] = await Promise.all([
      this.fetchCached(current.modelUrl, current.version),
      this.fetchLabels(current.labelsUrl),
    ]);

    this.labels = labels;
    this.session = await ort.InferenceSession.create(modelBuffer);
    this.currentVersion = current.version;
    return this.session;
  }

  private async fetchCurrentJson(): Promise<CurrentJson> {
    const response = await fetch(this.bustCache(this.getCurrentJsonUrl()));
    if (!response.ok) throw new Error('Failed to fetch current.json');
    return response.json() as Promise<CurrentJson>;
  }

  private bustCache(url: string): string {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}t=${Date.now()}`;
  }

  private async fetchCached(url: string, version: string): Promise<ArrayBuffer> {
    const cache = await caches.open(CACHE_NAME);

    // Evict stale entries from previous versions
    const keys = await cache.keys();
    for (const req of keys) {
      if (!req.url.includes(version)) {
        await cache.delete(req);
      }
    }

    const cached = await cache.match(url);
    if (cached) {
      return cached.arrayBuffer();
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch model: ${url}`);
    await cache.put(url, response.clone());
    return response.arrayBuffer();
  }

  private async fetchLabels(url: string): Promise<string[]> {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(url);
    if (cached) {
      return cached.json() as Promise<string[]>;
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch labels: ${url}`);
    await cache.put(url, response.clone());
    return response.json() as Promise<string[]>;
  }
}
